/**
 * File: BooksPrimitive.tsx
 * Path: apps/admin/src/shared/components/BooksPrimitive.tsx
 * Description: What every Accounting page shares: the switch between the
 * company's books and your own, the "set these books up" step, and how
 * amounts and balances are shown.
 *
 * BooksFrame loads the open profile and only renders the page once those
 * books exist, so the pages themselves never handle "not set up".
 */

import React from "react";
import {
  DEFAULT_YEAR_END_MONTH,
  formatCents,
  PROFILE_KIND_LABELS,
  type BooksProfile,
  type ProfileKind,
} from "@idk/config";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@idk/ui";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@idk/utils";
import { SectionCard } from "@/shared/components/SectionCard";
import { useBooksFrameController, useBooksPickerController, useSetUpBooksController } from "../hooks/useBooksFrameController";
import { monthName } from "../utils/booksFormatUtils";
import { TermHint } from "./TermHint";
import type { TermKey } from "../utils/termHintsData";
import { AmountText, type ChipTone, DescriptionText, labelTextClass, StatusChip, TitleText } from "@/shared/components/TextPrimitive";

export const thClass = `px-stack-sm py-stack-xs text-left ${labelTextClass}`;
export const thNumClass = `${thClass} text-right`;
export const tdClass = "px-stack-sm py-stack-xs align-top";
export const tdNumClass = `${tdClass} text-right tabular-nums whitespace-nowrap`;
export const tableFrame =
  "overflow-x-auto border border-border/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const notice = "space-y-stack-sm border border-dashed border-border/60 p-gutter-md text-sm";

/** "R 1 234,56", or an empty cell for nil. */
export const Money: React.FC<{ cents: number }> = ({ cents }) =>
  cents === 0 ? null : <>{formatCents(cents)}</>;

/**
 * The two sets of books as a drop-down. In the sidebar it fills the width
 * (the full "Books" label above the name); in a page header, on a phone, it
 * is the compact form. Switching changes every Accounting page at once.
 */
export const BooksPicker: React.FC<{ className?: string }> = ({ className }) => {
  const { kind, setKind, options, current } = useBooksPickerController();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Books: ${current.label}. Change which books are open`}
          className={cn(
            "flex min-h-11 w-full items-center justify-between gap-stack-sm border border-primary/60 bg-secondary/40 px-stack-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <span className="min-w-0">
            <span className={cn("block", labelTextClass)}>Books</span>
            <span className="block truncate text-sm font-bold">{current.label}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-none">
        {options.map((option) => (
          <DropdownMenuItem key={option.kind} onSelect={() => setKind(option.kind)} className="min-h-11 gap-stack-sm rounded-none">
            {kind === option.kind ? <Check className="h-4 w-4" aria-hidden /> : <span className="h-4 w-4" aria-hidden />}
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Page headers carry the same drop-down on a phone, where the sidebar is
 * hidden. From a tablet up the sidebar has it, so the header stays clear.
 */
export const BooksSwitch: React.FC = () => (
  <div className="w-full sm:hidden">
    <BooksPicker />
  </div>
);

export type PillTone = ChipTone;

/** A status on an accounting page: the shared tone chip. */
export const Pill: React.FC<{ tone: PillTone; children: React.ReactNode }> = ({ tone, children }) => (
  <StatusChip tone={tone}>{children}</StatusChip>
);

export const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => <TitleText>{children}</TitleText>;

/** A stat card: the label, the figure, one plain line, and a small chip. Used by Overview and Transactions. */
export const Figure: React.FC<{
  label: string;
  cents?: number;
  value?: string;
  explain: string;
  chip: { tone: PillTone; text: string };
  /** Adds an (i) beside the label that defines the term. */
  hint?: TermKey;
}> = ({ label, cents = 0, value, explain, chip, hint }) => (
  <div className="flex flex-col gap-stack-xs border border-border/50 border-t-2 border-t-primary bg-card p-gutter-md">
    <dt className={cn("flex items-center gap-stack-xs", labelTextClass)}>
      {label}
      {hint && <TermHint term={hint} name={label} />}
    </dt>
    <dd>
      <AmountText size="big">{value ?? (cents < 0 ? `−${formatCents(-cents)}` : formatCents(cents))}</AmountText>
    </dd>
    <dd className="text-sm text-muted-foreground">{explain}</dd>
    <dd>
      <Pill tone={chip.tone}>{chip.text}</Pill>
    </dd>
  </div>
);

/** A titled box with a one-line explanation, as on the Overview pages. */
export const Box: React.FC<{ title: string; caption: string; children: React.ReactNode; className?: string; actions?: React.ReactNode }> = ({
  title,
  caption,
  children,
  className,
  actions,
}) => (
  <section aria-label={title} className={cn("flex flex-col gap-stack-md rounded-none border border-border/50 bg-card p-gutter-md", className)}>
    <header className="space-y-stack-xs">
      <div className="flex items-center justify-between gap-stack-sm">
        <TitleText as="h3">{title}</TitleText>
        {actions}
      </div>
      <DescriptionText>{caption}</DescriptionText>
    </header>
    {children}
  </section>
);

const SetUpBooks: React.FC<{ kind: ProfileKind; profileId: string }> = ({ kind, profileId }) => {
  const { booksStart, setBooksStart, pending, canSubmit, submit } = useSetUpBooksController(kind, profileId);
  return (
    <SectionCard
      title={kind === "company" ? "Set up the company's books" : "Set up your own books"}
      description={
        kind === "company"
          ? "The books start with a chart of accounts for a South African service business, each account mapped to its ITR14 line. You can change any of it afterwards."
          : "Your own books are separate from the company's and only you can see them. They start with everyday categories, with the ITR12 items flagged."
      }
    >
      <form
        className="flex flex-wrap items-end gap-stack-md"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="space-y-stack-xs">
          <label htmlFor="books-start" className={cn("block", labelTextClass)}>
            First day of the books
          </label>
          <input
            id="books-start"
            type="date"
            value={booksStart}
            onChange={(e) => setBooksStart(e.target.value)}
            className="h-11 rounded-none border border-border/50 bg-background/50 px-stack-sm text-sm"
          />
        </div>
        <p className="max-w-prose-base text-xs text-muted-foreground">
          The year ends in {monthName(DEFAULT_YEAR_END_MONTH)}. Opening balances from Zoho Books are
          dated this day.
        </p>
        <Button type="submit" className="h-11 rounded-none" disabled={!canSubmit}>
          {pending ? "Setting up…" : "Set up these books"}
        </Button>
      </form>
    </SectionCard>
  );
};

/**
 * Renders `children` with the open books once they exist; otherwise the
 * loading state, the error, or the set-up step.
 */
export const BooksFrame: React.FC<{ children: (profile: BooksProfile) => React.ReactNode }> = ({
  children,
}) => {
  const frame = useBooksFrameController();
  if (frame.loading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading the books…
      </p>
    );
  }
  if (frame.failed) {
    return (
      <div role="alert" className={notice}>
        <p className="font-bold text-foreground">The books couldn't be loaded.</p>
        <Button
          type="button"
          variant="secondary"
          className="h-11 rounded-none"
          onClick={frame.retry}
          disabled={frame.retrying}
        >
          Try again
        </Button>
      </div>
    );
  }
  if (!frame.data) return <SetUpBooks kind={frame.kind} profileId={frame.profileId} />;
  return <>{children(frame.data)}</>;
};

/** One line under the header: whose books, from when, locked up to when. */
export const BooksSummary: React.FC<{ profile: BooksProfile }> = ({ profile }) => (
  <p className="text-xs text-muted-foreground">
    {profile.kind === "company" ? PROFILE_KIND_LABELS.company : "Your own books"} · from{" "}
    {profile.booksStart} · year ends {monthName(profile.yearEndMonth)} ·{" "}
    {profile.lockedThrough ? `locked up to ${profile.lockedThrough}` : "nothing locked"}
  </p>
);
